import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { DELETE_SHIFT, GET_SHIFTS } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';

const getDateRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 14);
  return { start: start.toISOString(), end: end.toISOString() };
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

const ScheduleManagement: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organization?.id;
  const dateRange = useMemo(() => getDateRange(), []);

  const { data, loading, error, refetch } = useQuery(GET_SHIFTS, {
    variables: {
      organizationId,
      startDate: dateRange.start,
      endDate: dateRange.end,
    },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteShift, { loading: deleting }] = useMutation(DELETE_SHIFT);

  const handleDelete = async (shiftId: string) => {
    await deleteShift({ variables: { id: shiftId } });
    await refetch();
  };

  const shifts = data?.shifts ?? [];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box component="main" sx={{ flex: 1, p: 3 }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Schedule Management
              </Typography>
              <Chip label="Live Data" color="success" variant="outlined" />
            </Box>

            {!organizationId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are not attached to an organization.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load schedule data.
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Shift Type</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell>Department</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {shifts.map((shift: any) => (
                              <TableRow key={shift.id}>
                                <TableCell>{shift.employee?.name || 'Unassigned'}</TableCell>
                                <TableCell>{formatDate(shift.startDate)}</TableCell>
                                <TableCell>{shift.shiftType}</TableCell>
                                <TableCell>
                                  {formatTime(shift.startDate)} - {formatTime(shift.endDate)}
                                </TableCell>
                                <TableCell>{shift.department}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={shift.status}
                                    size="small"
                                    color={shift.status === 'CONFIRMED' ? 'success' : 'warning'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Delete shift">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={deleting}
                                        onClick={() => handleDelete(shift.id)}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                            {shifts.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  No shifts found in the next 14 days.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default ScheduleManagement;
